import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ApplicationInsights, IExceptionTelemetry } from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { COMMON, OPERATIONS } from '../constants/app-insight-constants';
import { UserModel } from 'src/app/account/core/models/user.model';

@Injectable({
  providedIn: 'root'
})
export class AppLoggingService {

  private appInsights: ApplicationInsights;

  tenantInfo: any;
  currentUserInfo: any;

  constructor(
    private readonly router: Router
  ) {
    this.initializeApplicationInsights();
    this.setupRouterEvents();
  }

  private initializeApplicationInsights() {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsightsInstrumentationKey,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        correlationHeaderExcludedDomains: ['*.googleapis.com'],
      }
    });

    this.appInsights.loadAppInsights();
    this.addCustomTelemetryProperties();
  }

  private setupRouterEvents() {
    // Log page views automatically when route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.logPageView(event.urlAfterRedirects, event.urlAfterRedirects);
    });
  }

  setUserId(userId: string, accountId: string, currentUserValue?: UserModel) {
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
    
    this.currentUserInfo = {
      id: currentUserValue?.id,
      userName: currentUserValue?.userName,
      name: currentUserValue?.name,
      surname: currentUserValue?.surname,
      email: currentUserValue?.emailAddress
    };

    this.tenantInfo = {
      id: currentUserValue?.tenant?.id,
      name: currentUserValue?.tenant?.name,
      tenancyName: currentUserValue?.tenant?.tenancyName
    }
  }

  clearUserId() {
    this.appInsights.clearAuthenticatedUserContext();
  }

  // Log page view automatically when route changes
  private logPageView(name?: string, url?: string) {
    this.appInsights.trackPageView({
      name: name,
      uri: url
    });
  }

  // Log event
  logEvent(name: string, properties?: { [key: string]: any }) {
    const annotatedName: string = `${COMMON.AppInitials}_${name}`;
    this.appInsights.trackEvent({ name: annotatedName }, properties);
  }

  // Log trace
  logTrace(message: string, properties?: { [key: string]: any }) {
    const annotatedMessage: string = `${COMMON.AppInitials}_${message}`;
    this.appInsights.trackTrace({ message: annotatedMessage }, properties);
  }

  // Log exception
  logException(error: Error) {
    let annotatedError: Error = new Error();
    annotatedError = Object.assign(annotatedError, error);
    annotatedError.message = `${COMMON.AppInitials}_${OPERATIONS.Error}_${error.message}`;

    let exception: IExceptionTelemetry = {
      exception: annotatedError
    };

    this.appInsights.trackException(exception);
  }

  private addCustomTelemetryProperties() {
    let telemetryInitializer = (envelope: any) => {
      envelope.data.ApplicationPlatform = COMMON.ApplicationPlatform;
      envelope.data.ApplicationName = COMMON.AppName;
      envelope.data.ApplicationVersion = environment.appVersion;
      envelope.data.tenantName = this.tenantInfo ? this.tenantInfo.name : '';
      envelope.data.tenantId = this.tenantInfo ? this.tenantInfo.id : '';
      envelope.data.tenancyName = this.tenantInfo ? this.tenantInfo.tenancyName : '';
      envelope.data.userId = this.currentUserInfo ? this.currentUserInfo.id : '';
      envelope.data.userName = this.currentUserInfo ? this.currentUserInfo.userName : '';
      envelope.data.firstName = this.currentUserInfo ? this.currentUserInfo.name : '';
      envelope.data.lastName = this.currentUserInfo ? this.currentUserInfo.surname : '';
      envelope.data.email = this.currentUserInfo ? this.currentUserInfo.email : '';
    }
    this.appInsights.addTelemetryInitializer(telemetryInitializer);
  }
}
